import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// TYPES
// ============================================

export type GuildRole = 'leader' | 'officer' | 'member';

export interface Guild {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  color: string | null;
  telegramChatLink: string | null;
  memberCount: number;
  weeklyXp: number;
  userRole?: GuildRole;
  isMember: boolean;
}

export interface GuildMember {
  id: string;
  oderId: string;
  role: GuildRole;
  contribution: number;
  joinedAt: string;
  userName: string;
  userAvatar: string | null;
  characterName: string | null;
  characterLevel: number;
  archetype: string | null;
  weeklyXp: number;
}

export interface LeaderboardEntry {
  rank: number;
  oderId: string;
  userName: string;
  characterName: string | null;
  characterLevel: number;
  archetype: string | null;
  totalXp: number;
  weeklyXp: number;
  guildName?: string;
}

export interface GuildLeaderboardEntry {
  rank: number;
  guildId: string;
  guildName: string;
  emoji: string | null;
  memberCount: number;
  weeklyXp: number;
  totalXp: number;
}

// ============================================
// API CALLS
// ============================================

export async function getAllGuilds(): Promise<{ guilds: Guild[] }> {
  return request('/social/guilds');
}

export async function getMyGuild(): Promise<{ guild: Guild | null }> {
  return request('/social/guilds/my');
}

export async function getGuildDetails(guildId: string): Promise<{ guild: Guild }> {
  return request(`/social/guilds/${guildId}`);
}

export async function getGuildMembers(guildId: string): Promise<{ members: GuildMember[] }> {
  return request(`/social/guilds/${guildId}/members`);
}

export async function joinGuild(guildId: string): Promise<{ membership: GuildMember }> {
  return request(`/social/guilds/${guildId}/join`, { method: 'POST' });
}

export async function leaveGuild(guildId: string): Promise<{ message: string }> {
  return request(`/social/guilds/${guildId}/leave`, { method: 'POST' });
}

export async function getGlobalLeaderboard(limit?: number): Promise<{ leaderboard: LeaderboardEntry[] }> {
  const params = limit ? `?limit=${limit}` : '';
  return request(`/social/leaderboard${params}`);
}

export async function getGuildLeaderboard(): Promise<{ leaderboard: GuildLeaderboardEntry[] }> {
  return request('/social/leaderboard/guilds');
}
